import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import JobNewForm from "../components/JobNewForm";
import Modal from "../components/Modal";
import type { Job } from "../types/database";
import { format } from "date-fns";

type SortField =
  | "project_number"
  | "title"
  | "prep_date"
  | "ship_date"
  | "return_date"
  | "status";
type SortDirection = "asc" | "desc";

export default function Jobs() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "all" | "upcoming" | "in_progress" | "completed"
  >("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState<"grid" | "list">("list");
  const [sortField, setSortField] = useState<SortField>("prep_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [formData, setFormData] = useState<{
    formData: {
      project_number: string;
      title: string;
      location: string;
      prep_date: string;
      ship_date: string;
      start_date: string;
      end_date: string;
      return_date: string;
      status: "upcoming" | "in_progress" | "completed" | "cancelled";
      description: string;
      client: string;
    };
    locationDetails: {
      name: string;
      address?: string;
      city?: string;
      county?: string;
      postcode?: string;
      country?: string;
    };
  } | null>(null);

  // Check if modal should be open based on URL parameter
  useEffect(() => {
    const modalParam = searchParams.get("modal");
    if (modalParam === "new") {
      setIsModalOpen(true);
      // Remove the modal parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("modal");
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams]);

  // Fetch jobs data
  const {
    data: jobs = [],
    isLoading,
    error,
  } = useQuery<Job[]>({
    queryKey: ["jobs", currentOrganization?.id],
    queryFn: () => jobService.getJobs(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Mutation for updating job status

  // Filter jobs based on active tab and search term
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const matchesTab = activeTab === "all" || job.status === activeTab;
      const matchesSearch =
        job.project_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesTab && matchesSearch;
    });
  }, [jobs, activeTab, searchTerm]);

  // Sort jobs based on sortField and sortDirection
  const sortedJobs = useMemo(() => {
    return [...filteredJobs].sort((a, b) => {
      let aValue: string | number = a[sortField];
      let bValue: string | number = b[sortField];

      // Handle date fields
      if (sortField.includes("date")) {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredJobs, sortField, sortDirection]);

  // Handle sort click
  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy");
  };

  const parseLocation = (locationString: string) => {
    if (!locationString) return "Not set";
    try {
      const locationData = JSON.parse(locationString);
      return locationData.name || "Unknown location";
    } catch {
      // If it's not valid JSON, return the string as is
      return locationString;
    }
  };

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;

    return (
      <span className="ml-1 inline-block">
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const handleOpenFullScreen = () => {
    setIsModalOpen(false);
    setIsFullScreen(true);
  };

  const handleFormDataChange = useCallback(
    (data: {
      formData: {
        project_number: string;
        title: string;
        location: string;
        prep_date: string;
        ship_date: string;
        start_date: string;
        end_date: string;
        return_date: string;
        status: "upcoming" | "in_progress" | "completed" | "cancelled";
        description: string;
        client: string;
      };
      locationDetails: {
        name: string;
        address?: string;
        city?: string;
        county?: string;
        postcode?: string;
        country?: string;
      };
    }) => {
      setFormData(data);
    },
    []
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreen(false);
  };

  const handleSuccess = () => {
    setIsModalOpen(false);
    setIsFullScreen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          Error loading jobs. Please try again later.
        </div>
      </div>
    );
  }

  if (isFullScreen) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Create New Job
                </h1>
                <p className="text-gray-600 mt-1">
                  Add a new job to your organization
                </p>
              </div>
              <button
                onClick={handleCloseFullScreen}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6">
              <JobNewForm
                onSuccess={handleSuccess}
                onCancel={handleCloseFullScreen}
                isModal={false}
                initialFormData={formData || undefined}
                onFormDataChange={handleFormDataChange}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your events and productions
          </p>
        </div>
        <Link
          to={`/org/${orgId}/jobs/new`}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          + Add Job
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("all")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "all"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab("upcoming")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "upcoming"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab("in_progress")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "in_progress"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setActiveTab("completed")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "completed"
                ? "border-primary text-primary"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Completed
          </button>
        </nav>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search jobs..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            className={`px-3 py-2 ${
              view === "grid"
                ? "bg-primary text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setView("grid")}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
          </button>
          <button
            className={`px-3 py-2 ${
              view === "list"
                ? "bg-primary text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setView("list")}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 10h16M4 14h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500">
        Showing {sortedJobs.length} jobs
      </div>

      {/* Grid View */}
      {view === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedJobs.map((job) => {
            const detailUrl = `/org/${orgId}/jobs/${job.id}`;

            return (
              <Link
                key={job.id}
                to={detailUrl}
                className="block bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <span className="text-sm font-medium text-gray-500">
                    {job.project_number}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      job.status === "upcoming"
                        ? "bg-yellow-100 text-yellow-800"
                        : job.status === "in_progress"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {job.status === "in_progress"
                      ? "In Progress"
                      : job.status.charAt(0).toUpperCase() +
                        job.status.slice(1)}
                  </span>
                </div>

                {/* Job Title */}
                <h3 className="font-semibold text-gray-900 text-lg mb-3">
                  {job.title}
                </h3>

                {/* Location */}
                <div className="mt-2 flex items-start text-sm">
                  <svg
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-gray-600">
                    {parseLocation(job.location)}
                  </span>
                </div>

                {/* Dates Section */}
                <div className="mt-4 pt-3 border-t border-gray-100">
                  {/* Prep Date */}
                  <div className="flex items-center text-sm">
                    <svg
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-gray-700">
                      <span className="font-medium">Prep:</span>{" "}
                      {formatDate(job.prep_date)}
                    </span>
                  </div>

                  {/* Ship Date */}
                  <div className="mt-1.5 flex items-center text-sm">
                    <svg
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-700">
                      <span className="font-medium">Ship:</span>{" "}
                      {formatDate(job.ship_date)}
                    </span>
                  </div>

                  {/* Return Date */}
                  <div className="mt-1.5 flex items-center text-sm">
                    <svg
                      className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3"
                      />
                    </svg>
                    <span className="text-gray-700">
                      <span className="font-medium">Return:</span>{" "}
                      {formatDate(job.return_date)}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left bg-gray-50">
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("project_number")}
                >
                  Project # {renderSortIndicator("project_number")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("title")}
                >
                  Project Name {renderSortIndicator("title")}
                </th>
                <th className="p-4">Location</th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("prep_date")}
                >
                  Prep Date {renderSortIndicator("prep_date")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("ship_date")}
                >
                  Ship Date {renderSortIndicator("ship_date")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("return_date")}
                >
                  Return Date {renderSortIndicator("return_date")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("status")}
                >
                  Status {renderSortIndicator("status")}
                </th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job) => {
                const detailUrl = `/org/${orgId}/jobs/${job.id}`;

                return (
                  <tr
                    key={job.id}
                    className="border-t hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(detailUrl)}
                    onDoubleClick={() => navigate(detailUrl)}
                  >
                    <td className="p-4 font-medium">{job.project_number}</td>
                    <td className="p-4">{job.title}</td>
                    <td className="p-4">{parseLocation(job.location)}</td>
                    <td className="p-4">{formatDate(job.prep_date)}</td>
                    <td className="p-4">{formatDate(job.ship_date)}</td>
                    <td className="p-4">{formatDate(job.return_date)}</td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          job.status === "upcoming"
                            ? "bg-yellow-100 text-yellow-800"
                            : job.status === "in_progress"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {job.status === "in_progress"
                          ? "In Progress"
                          : job.status.charAt(0).toUpperCase() +
                            job.status.slice(1)}
                      </span>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex space-x-2">
                        <Link
                          to={detailUrl}
                          className="text-primary hover:underline"
                        >
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Create New Job"
        size="lg"
      >
        <div className="relative">
          {/* Expand to full screen button */}
          <button
            onClick={handleOpenFullScreen}
            className="absolute -top-2 -right-2 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            title="Expand to full screen"
          >
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
              />
            </svg>
          </button>

          <JobNewForm
            onSuccess={handleSuccess}
            onCancel={handleCloseModal}
            isModal={true}
            initialFormData={formData || undefined}
            onFormDataChange={handleFormDataChange}
          />
        </div>
      </Modal>
    </div>
  );
}
